// src/components/layout/Sidebar.js
import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Calendar, Users, Target, FileText, Briefcase, FilePlus2, Clock, Settings, Receipt, BarChart2, Mail, Send, UserPlus, AlertTriangle, LogOut, HelpCircle, Map, CalendarDays, Flag, Repeat, BookOpen } from 'lucide-react'; // recinv-sidebar-6f4d2b98 · sopweb-bdb7afcd BookOpen
import { can, PERMISSIONS, canOpenTimesheets } from '../../utils/permissions'; // tsmgr-5a2c8e71
import GodModeSwitcher from '../admin/GodModeSwitcher';
import SupportModal from '../common/SupportModal'; 
import { doc, updateDoc, onSnapshot } from 'firebase/firestore'; // r1-p1-docterm-sidebar-a7f3e9c2
import { db } from '../../firebase/config';
import logo from '../../assets/logo.jpg';
import { resolveReportViewer } from '../../utils/quoteReporting'; // qreport-5b8e2c64
import { DOC_TYPES, resolveDocTerminology } from '../../utils/quoteSchema'; // r1-p1-docterm-sidebar-a7f3e9c2 
import { showSopsMenu, toReadCount } from '../../utils/sopsUi'; // sopweb-bdb7afcd · sopread-7e4c2a93
import { myAccessQuery } from '../sops/sopsApi'; // sopread-7e4c2a93

// --- CONFIGURATION ---
const ADMIN_HOME_ORG_ID = 'admin_hq'; 

const Sidebar = ({ isMobileMenuOpen, closeMobileMenu, currentUserProfile, newLeadCount, newFlaggedCount }) => {
    const [showGodMode, setShowGodMode] = useState(false);
    const [isSupportOpen, setIsSupportOpen] = useState(false);
    // r1-p1-docterm-sidebar-a7f3e9c2
    // Org-default document terminology for the aggregate nav label (Quotes/Estimates).
    // Reads quoteTerminologyDefault off the org doc — same path/shape CrmApp uses for
    // companySettings. Missing org / loading / absent field → DOC_TYPES.QUOTE → "Quotes".
    const [quoteTerminologyDefault, setQuoteTerminologyDefault] = useState(DOC_TYPES.QUOTE);
    // sopweb-bdb7afcd — the SOPs switch rides on this same listener (no extra read). Missing = off.
    const [sopsLibraryEnabled, setSopsLibraryEnabled] = useState(false);
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!orgId) return;
        const unsub = onSnapshot(doc(db, `organizations/${orgId}`), (d) => {
            if (d.exists()) setQuoteTerminologyDefault(d.data()?.quoteTerminologyDefault || DOC_TYPES.QUOTE);
            setSopsLibraryEnabled(d.exists() && d.data()?.sopsLibraryEnabled === true);
        });
        return () => unsub();
    }, [currentUserProfile?.organizationId]);

    // sopread-7e4c2a93 — the SOPs badge: how many SOPs this person still has to read. ONE live query of
    // their own access records (a handful of documents per sign-in, then one per change), only while
    // SOPs show in the menu. A refusal (e.g. an inactive account) just hides the badge.
    const [sopsToRead, setSopsToRead] = useState(0);
    const sopsMenuOn = showSopsMenu(currentUserProfile, { sopsLibraryEnabled });
    const sopsUid = currentUserProfile?.id || currentUserProfile?.uid || null;
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        if (!sopsMenuOn || !orgId || !sopsUid) { setSopsToRead(0); return undefined; }
        const unsub = onSnapshot(myAccessQuery(orgId, sopsUid),
            (snap) => setSopsToRead(toReadCount(snap.docs.map((d) => d.data()))),
            (err) => { console.warn('sopread: SOPs badge unavailable', err?.code || err); setSopsToRead(0); });
        return () => unsub();
    }, [sopsMenuOn, currentUserProfile?.organizationId, sopsUid]);

    // Sanity Check Log
    useEffect(() => { console.log("✅ SIDEBAR LOADED (SECURE VERSION)"); }, []);

    // --- SECURITY FIX: STRICT IMPERSONATION CHECK ---
    // Only show the banner if:
    // 1. I am explicitly marked as a Super Admin in Firestore
    // 2. AND I am currently viewing an organization that is NOT HQ
    const isSuperAdmin = currentUserProfile?.isSuperAdmin === true;
    
    const isImpersonating = 
        isSuperAdmin && 
        currentUserProfile?.organizationId !== ADMIN_HOME_ORG_ID;

    // --- FULL NAV ITEMS LIST ---
    // r1-p1-docterm-sidebar-a7f3e9c2 — resolve org-default plural once for the Quotes/Estimates nav label
    const { wordPlural: quotesNavLabel } = resolveDocTerminology(quoteTerminologyDefault);
    const navItems = [
        // Public Items
        { to: '/home', icon: <Home size={20} />, text: 'Home' },
        { to: '/calendar', icon: <Calendar size={20} />, text: 'Calendar' },
        { to: '/email', icon: <Mail size={20} />, text: 'Inbox' },

        // PHASE 1.6 VERIFIER: r1-16-web-sidebar-16a3f2b8
        // Flagged-note triage inbox. No permission gate — every user has
        // their own flags (or empty state). Public entry, like Home/Calendar.
        // PHASE 1.6 VERIFIER: r1-16-web-sidebar-badge-16a3f2b8
        // Badge renders when there's at least one unresolved flag targeted
        // at the current user (count is live via the onSnapshot listener in
        // ProtectedCrmLayout). Reuses the existing badge styling from Leads.
        { to: '/flagged', icon: <Flag size={20} />, text: 'Flagged', badge: newFlaggedCount },
        
        // Restricted Items
        { 
            to: '/marketing', icon: <Send size={20} />, text: 'Email Marketing', 
            permission: PERMISSIONS.MANAGE_MARKETING 
        },
        { 
            to: '/customers', icon: <Users size={20} />, text: 'Customers',
            permission: PERMISSIONS.VIEW_CUSTOMERS 
        },
        
        // HR SECTION
        { 
            to: '/employees', icon: <Users size={20} />, text: 'HR / Team', 
            permission: PERMISSIONS.VIEW_HR 
        }, 
        { 
            to: '/hiring', icon: <UserPlus size={20} />, text: 'Hiring', 
            permission: PERMISSIONS.MANAGE_HIRING 
        },

        { 
            to: '/leads', icon: <Target size={20} />, text: 'Leads',
            permission: PERMISSIONS.MANAGE_LEADS,
            badge: newLeadCount 
        },
        { 
            to: '/quotes', icon: <FileText size={20} />, text: quotesNavLabel, // r1-p1-docterm-sidebar-a7f3e9c2
            permission: PERMISSIONS.VIEW_FINANCIALS 
        },
        { 
            to: '/jobs', icon: <Briefcase size={20} />, text: 'Jobs',
            permission: PERMISSIONS.VIEW_JOBS 
        },
        { 
            to: '/routes', icon: <Map size={20} />, text: 'Routes',
            permission: PERMISSIONS.VIEW_ROUTES 
        },
        { 
            to: '/schedule/plan', icon: <CalendarDays size={20} />, text: 'Weekly Planner',
            permission: PERMISSIONS.MANAGE_SCHEDULE 
        },
        {
            to: '/invoices', icon: <FilePlus2 size={20} />, text: 'Invoices',
            permission: PERMISSIONS.VIEW_FINANCIALS
        },
        { // recinv-sidebar-6f4d2b98
            to: '/recurring-invoices', icon: <Repeat size={20} />, text: 'Recurring Invoices',
            permission: PERMISSIONS.VIEW_FINANCIALS
        },
        { 
            to: '/timesheets', icon: <Clock size={20} />, text: 'Timesheets',
            permission: PERMISSIONS.COMPLETE_JOBS,
            // tsmgr-5a2c8e71 — ALSO approvers (Managers + bare Owner/System Admin strings), who
            // lack COMPLETE_JOBS. Same helper as the /timesheets route in CrmApp.
            alsoShowIf: (profile) => canOpenTimesheets(profile),
        },
        { 
            to: '/expenses', icon: <Receipt size={20} />, text: 'Expenses',
            permission: PERMISSIONS.MANAGE_EXPENSES 
        },
        { 
            to: '/reports', icon: <BarChart2 size={20} />, text: 'Reports',
            permission: PERMISSIONS.VIEW_REPORTS,
            // qreport-5b8e2c64 — ALSO shown to a Sales Consultant, whose Reports
            // page lists only their own Estimates report. No permission change:
            // VIEW_REPORTS still gates every other report.
            alsoShowIf: (profile) => resolveReportViewer(profile).access === 'scoped',
        },
        // sopweb-bdb7afcd — SOPs: in the list ONLY when the company's switch is exactly on. Not via
        // alsoShowIf — that can only add an item that fails its permission, never hide one. Same check as
        // the /sops route in CrmApp. sopread-7e4c2a93 — for EVERYONE whose role can open SOPs (My SOPs);
        // Manage library is a tab inside for Manager and up. Badge = SOPs still to read.
        ...(sopsMenuOn ? [{ to: '/sops', icon: <BookOpen size={20} />, text: 'SOPs', badge: sopsToRead }] : []),
        { 
            to: '/settings', icon: <Settings size={20} />, text: 'Settings' 
        },
    ];

    const handleExitGodMode = async () => {
        if(!window.confirm("Return to HQ?")) return;
        try {
            const userRef = doc(db, 'users', currentUserProfile.id);
            await updateDoc(userRef, {
                organizationId: ADMIN_HOME_ORG_ID,
                organizationName: 'Spatchy HQ' 
            });
            window.location.reload();
        } catch (error) {
            alert("Error returning to HQ: " + error.message);
        }
    };

    return (
        <>
            {/* --- GOD MODE BANNER (SECURED) --- */}
            {isImpersonating && (
                <div className="fixed top-0 left-0 w-full bg-red-600 text-white z-[60] px-4 py-2 shadow-xl flex justify-between items-center h-[40px]">
                    <div className="flex items-center gap-2 font-bold uppercase tracking-wider text-xs md:text-sm truncate">
                        <AlertTriangle size={18} className="text-yellow-300 flex-shrink-0" />
                        <span className="truncate">VIEWING: <span className="underline text-yellow-300">{currentUserProfile.organizationName}</span></span>
                    </div>
                    <button 
                        onClick={handleExitGodMode}
                        className="bg-white text-red-600 px-3 py-1 rounded text-xs font-bold hover:bg-gray-100 flex items-center gap-2 flex-shrink-0 ml-2"
                    >
                        <LogOut size={12} /> EXIT
                    </button>
                </div>
            )}

            {/* --- MODALS --- */}
            {showGodMode && <GodModeSwitcher currentUserProfile={currentUserProfile} onClose={() => setShowGodMode(false)} />}
            <SupportModal isOpen={isSupportOpen} onClose={() => setIsSupportOpen(false)} currentUserProfile={currentUserProfile} />

            {/* --- MOBILE BACKDROP --- */}
            {isMobileMenuOpen && ( 
                <div 
                    className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm transition-opacity" 
                    onClick={closeMobileMenu}
                /> 
            )}
            
            {/* --- SIDEBAR DRAWER --- */}
            <aside className={`
                fixed top-0 left-0 h-full w-64 bg-white border-r border-gray-200 z-50 
                transform transition-transform duration-300 ease-in-out 
                ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} 
                md:translate-x-0 
                ${isImpersonating ? 'pt-[40px]' : ''} 
                flex flex-col shadow-2xl md:shadow-none
            `}>
                
                {/* 1. HEADER */}
                <div className="p-4 border-b h-[75px] flex items-center justify-between flex-shrink-0 bg-white">
                    <h1 
                        className="text-2xl font-bold text-gray-900 cursor-pointer select-none flex items-center gap-3"
                        onDoubleClick={() => {
                            // SECURITY FIX: Only allow God Mode switch if isSuperAdmin is true
                            if (isSuperAdmin) {
                                setShowGodMode(true);
                            }
                        }}
                        title={isSuperAdmin ? "Double-click for Admin" : "Spatchy"} 
                    >
                        <img src={logo} alt="Spatchy" className="w-12 h-12 object-contain rounded-lg shadow-sm" />
                        <span className="tracking-tight">Spatchy</span>
                    </h1>
                    
                    <button onClick={closeMobileMenu} className="md:hidden text-gray-500 hover:bg-gray-100 p-1 rounded-full">
                        <LogOut size={20} className="rotate-180"/> 
                    </button>
                </div>
                
                {/* 2. NAVIGATION */}
                <nav className="flex-1 overflow-y-auto p-3 space-y-1">
                    {navItems.map((item) => {
                        if (item.permission && !can(currentUserProfile, item.permission)
                            && !(item.alsoShowIf && item.alsoShowIf(currentUserProfile))) return null; // qreport-5b8e2c64
                        
                        return (
                            <NavLink 
                                key={item.to} 
                                to={item.to} 
                                id={`nav-${item.text.toLowerCase().replace(/[^a-z0-9]/g, '-')}`} 
                                onClick={closeMobileMenu}
                                className={({ isActive }) => `
                                    flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-sm transition-all
                                    ${isActive 
                                        ? 'bg-blue-50 text-blue-700 shadow-sm' 
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}
                                `}
                            >
                                {item.icon}
                                <span className="truncate">{item.text}</span>
                                {item.badge > 0 && (
                                    <span className="ml-auto bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center shadow-sm">
                                        {item.badge}
                                    </span>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>

                {/* 3. FOOTER */}
                <div className="p-3 bg-gray-50 border-t border-gray-200 mt-auto">
                    <button 
                        onClick={() => { setIsSupportOpen(true); closeMobileMenu(); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium text-blue-700 bg-white border border-blue-100 hover:bg-blue-50 hover:shadow-sm transition-all"
                    >
                        <HelpCircle size={20} />
                        <span>Help & Support</span>
                    </button>
                </div>

            </aside>
        </>
    );
};

export default Sidebar;